import React, { useCallback } from 'react';
import { Plus } from 'lucide-react';
import Badge from '../../../../ui/Badge';
import ButtonSpinner from '../../../../ui/ButtonSpinner';
import CardOverlays from '../../../../ui/CardOverlays';
import CardTile from '../../../../ui/CardTile';
import { useLongPress } from '../../../../hooks/useLongPress';
import { formatPrice } from '../../utils/formatPrice';

/**
 * MobileResultTile — Treffer-Kachel des mobilen Hinzufügen-Modus (Suche und Vorschläge).
 * Eigene Komponente, weil die Long-Press-Geste einen Hook braucht und der nicht in einer
 * Schleife laufen darf.
 *
 * Keine Namenszeile unter dem Bild: der Name steht auf der Karte selbst, eine Textzeile
 * kostet nur Höhe. Screenreader bekommen ihn über `clickLabel` und den Alt-Text.
 *
 * @param {object} entry — Treffer (CardSearchEntryDTO)
 * @param {boolean} inDeck — Badge „✓ im Deck"
 * @param {boolean} selected — Kandidaten-Markierung
 * @param {boolean} canAdd — Plus-Knopf als sichtbares Overlay zeigen
 * @param {boolean} busy — Aktion der Kachel läuft: die Kachel tritt zurück, Plus zeigt einen
 *   Spinner und löst nichts aus, ein Langdruck bleibt wirkungslos. Antippen (Viewer öffnen)
 *   bleibt möglich. Der Knopf trägt `aria-busy` statt `disabled`, damit er den Fokus behält
 * @param {(entry) => void} onTap — Antippen der Kachel
 * @param {(entry) => void} [onLongPress] — Langdruck; fehlt der Callback, hängen keine
 *   Gesten-Handler an der Kachel
 * @param {(entry) => void} onAdd — Plus
 * @param {React.ReactNode} [children] — Inhalt einer weiteren `CardTile.Row` unter Preis
 *   und Badges
 */
export default function MobileResultTile({
  entry,
  inDeck = false,
  selected = false,
  canAdd = false,
  busy = false,
  onTap,
  onLongPress,
  onAdd,
  children,
}) {
  const handleLongPress = useCallback(() => {
    if (!busy) onLongPress?.(entry);
  }, [busy, onLongPress, entry]);
  // Die Geste liegt auf dem Kachel-Wrapper, nicht auf dem Klick-Ziel von ui/CardTile:
  // der Druck kann auf dem Kartenbild beginnen, dessen pointer-events abgeschaltet sind.
  const longPressHandlers = useLongPress(handleLongPress);

  return (
    <div
      className={`mobile-add-tile${busy ? ' mobile-add-tile--busy' : ''}`}
      aria-busy={busy || undefined}
      {...(onLongPress ? longPressHandlers : {})}
    >
      <CardTile selected={selected} onClick={() => onTap(entry)} clickLabel={entry.name}>
        <CardTile.Image src={entry.imageUrl} backSrc={entry.imageUrlBack || undefined} alt={entry.name} orientation={entry.imageOrientation}>
          {canAdd && (
            <CardOverlays>
              <CardOverlays.Actions alwaysVisible>
                <CardOverlays.Button
                  icon={busy ? ButtonSpinner : Plus}
                  label={`${entry.name} hinzufügen`}
                  aria-busy={busy || undefined}
                  onClick={() => { if (!busy) onAdd(entry); }}
                />
              </CardOverlays.Actions>
            </CardOverlays>
          )}
        </CardTile.Image>
        <CardTile.Info>
          <CardTile.Row>
            <CardTile.Price>{formatPrice(entry.price)}</CardTile.Price>
            <span className="mobile-add-tile__badges">
              {entry.ownedQuantity > 0 && <Badge tone="accent">im Besitz</Badge>}
              {inDeck && <Badge>✓ im Deck</Badge>}
            </span>
          </CardTile.Row>
          {children && <CardTile.Row>{children}</CardTile.Row>}
        </CardTile.Info>
      </CardTile>
    </div>
  );
}
